/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/definition 层；
 * 2. 负责定义“特征参数真相”的统一抽象基类；
 * 3. 后续所有具体特征定义都应继承该基类，而不是直接把命令参数散落到各处。
 *
 * 文件职责：
 * 1. 约束特征定义对象必须具备 featureKind 与 version；
 * 2. 统一提供快照克隆能力，避免上层直接持有可变引用；
 * 3. 为 Prototype / EditSession / ResultObject 提供统一的定义读取入口。
 */

/**
 * 特征定义快照。
 */
export interface FeatureDefinitionSnapshot {
    /** 特征语义类型。 */
    featureKind: string;
    /** 定义结构版本号。 */
    version: number;
}

/**
 * 特征定义抽象基类。
 *
 * 设计说明：
 * 1. 定义对象只负责承载结构化参数真相，不负责执行几何逻辑；
 * 2. 所有写入构造函数的快照都会被克隆，避免外部共享引用污染编辑会话；
 * 3. 具体特征定义可在子类中暴露更细粒度的读取方法。
 */
export abstract class FeatureDefinition<TSnapshot extends FeatureDefinitionSnapshot = FeatureDefinitionSnapshot> {
    /** 特征定义快照。 */
    private readonly snapshot: TSnapshot;

    /**
     * @param snapshot 特征定义快照。
     */
    protected constructor(snapshot: TSnapshot) {
        this.snapshot = structuredClone(snapshot);
    }

    /**
     * 获取特征语义类型。
     *
     * @returns 特征语义类型。
     */
    public getFeatureKind(): TSnapshot['featureKind'] {
        return this.snapshot.featureKind;
    }

    /**
     * 获取定义结构版本号。
     *
     * @returns 定义结构版本号。
     */
    public getVersion(): number {
        return this.snapshot.version;
    }

    /**
     * 获取当前定义快照。
     *
     * @returns 克隆后的定义快照。
     */
    public toSnapshot(): TSnapshot {
        return structuredClone(this.snapshot);
    }
}
